<script setup lang="ts">
import type { ContentProductPackage } from '~/models/Content/ContentProductPackage'
import type { ContentProductHeader } from '~/models/Content/ContentProductHeader'
import type { ContentProduct } from '~/models/Content/ContentProduct'
import type { ContentPackage } from '~/models/Content/ContentPackage'
import type { ContentFolder } from '~/models/Content/ContentFolder'
import { ref, watch } from 'vue'
import { faMinus, faPlus } from '@fortawesome/duotone-regular-svg-icons'
import useAccordion from '~/composables/useAccordion'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'

const props = withDefaults(defineProps<{
  header: ContentProductHeader | ContentProductPackage | ContentProduct | ContentFolder
  section: ContentPackage
  id: string
  open?: boolean
  titleStyle?: string
}>(), {
  open: false,
  titleStyle: 'font-calistoga fluid-text-2xl',
})

const emit = defineEmits<{ openAccordion: [locationId: number[]] }>()

const { getAccordion, toggleAccordion, addAccordion } = useAccordion()
const isOpen = ref(false)

const toggle = () => {
  isOpen.value = toggleAccordion(props.header.locationId, props.section.locationId)
  emit('openAccordion', getAccordion(props.header.locationId))
}

watch(props, () => {
  isOpen.value = props.open || getAccordion(props.header.locationId).includes(props.section.locationId)
  if (isOpen.value) {
    addAccordion(props.header.locationId, props.section.locationId)
  }
}, { immediate: true })
</script>

<template>
  <div
    :id="id"
    class="max-w-screen-au relative mx-auto w-full px-4 sm:px-8"
  >
    <h2 class="w-full" :class="titleStyle">
      <label
        :id="`button-${id}`"
        :aria-expanded="isOpen"
        :aria-controls="`section-${id}`"
        class="group flex w-full cursor-pointer items-center justify-between pt-12 focus-visible:ring"
        :class="{
          'pb-4': section.intro,
          'pb-8': !section.intro,
          'before:absolute before:inset-0': !isOpen,
        }"
      >
        <div class="flex items-center">
          <span
            class="mr-4 transition group-hover:underline group-focus-visible:underline"
            v-text="section.title"
          />
        </div>
        <button
          class="flex size-10 items-center justify-center rounded-full transition hover:outline-2 focus-visible:outline"
          @click="toggle"
        >
          <font-awesome-icon
            class="transition-transform duration-300 ease-out"
            :class="isOpen ? 'invisible absolute rotate-90' : 'rotate-0'"
            :icon="faPlus"
          />
          <font-awesome-icon
            class="transition-transform duration-300 ease-out"
            :class="!isOpen ? 'invisible absolute -rotate-90' : 'rotate-0'"
            :icon="faMinus"
          />
        </button>
      </label>
    </h2>
    <div v-if="section.intro" class="max-w-prose pb-8 text-xl font-semibold">
      <RichTextRenderer :text="section.intro" />
    </div>
    <div
      :id="`section-${id}`"
      v-disable-tabindex="!isOpen"
      role="region"
      :aria-labelledby="`button-${id}`"
      class="z-10 overflow-x-visible overflow-y-clip pb-16"
      :class="!isOpen ? 'pointer-events-none w-full h-20 max-w-screen-au opacity-70' : 'transition-opacity'"
      :aria-hidden="!isOpen"
    >
      <slot />
    </div>
  </div>
</template>
